import React from 'react';
import { ArrowLeft, ArrowRight, Save, Loader2, X } from 'lucide-react';

export interface WizardFooterProps {
  onPrevious?: () => void;
  onNext: () => void;
  /** @deprecated Le bouton est toujours cliquable, la validation se fait via showErrors */
  canProceed?: boolean;
  hideWhenTutorial?: boolean;
  isTutorialActive?: boolean;
  previousLabel?: string;
  nextLabel?: string;
  isSubmitting?: boolean;
  formId?: string;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
  saveLabel?: string;
  modeToggle?: React.ReactNode;
  /** Action d'aide affichée à gauche de Save/Next dans le groupe droit */
  helpAction?: React.ReactNode;
  fixedToViewport?: boolean;
  leftOffset?: string;
  /** Right offset in pixels (used for dynamic panel width) */
  rightOffsetPx?: number;
  currentStep?: number;
  totalSteps?: number;
  /** Callback pour annuler/quitter le wizard (affiché quand pas de bouton Précédent) */
  onCancel?: () => void;
}

export const WizardFooter: React.FC<WizardFooterProps> = ({
  onPrevious,
  onNext,
  canProceed: _,
  hideWhenTutorial = false,
  isTutorialActive = false,
  previousLabel = 'Précédent',
  nextLabel = 'Suivant',
  isSubmitting = false,
  formId,
  onSaveDraft,
  isSavingDraft = false,
  saveLabel = 'Sauvegarder',
  modeToggle,
  helpAction,
  fixedToViewport = false,
  leftOffset = '',
  rightOffsetPx,
  currentStep,
  totalSteps,
  onCancel,
}) => {
  // Masquer si tutoriel actif et option activée
  if (hideWhenTutorial && isTutorialActive) {
    return null;
  }

  // Offset par défaut : pleine largeur après la sidebar (md:tw-left-64 = 16rem = tw-w-64 de la sidebar admin)
  // Les formulaires intégrés dans les onglets passent un leftOffset custom pour s'aligner au contenu de l'onglet
  const defaultOffset = 'tw-left-0 tw-right-0 md:tw-left-64';
  const positionClasses = fixedToViewport
    ? `tw-fixed tw-bottom-0 ${leftOffset || defaultOffset}`
    : 'tw-sticky tw-bottom-0';

  return (
    <div
      className={`${positionClasses} tw-bg-white tw-z-20 tw-transition-[right] tw-duration-300 tw-ease-in-out tw-pb-[env(safe-area-inset-bottom)]${!fixedToViewport ? ' tw-border-t tw-border-gray-200' : ''}`}
      style={rightOffsetPx ? { right: rightOffsetPx } : undefined}
    >
      <div className="tw-max-w-7xl tw-mx-auto tw-px-2 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-flex tw-items-center tw-justify-between tw-gap-2 sm:tw-gap-4">
        <div className="tw-flex-shrink-0">
          {onPrevious ? (
            <button
              type="button"
              onClick={onPrevious}
              disabled={isSubmitting}
              className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-6 tw-py-2 sm:tw-py-3 tw-bg-transparent tw-text-gray-600 tw-text-base tw-font-medium tw-rounded-xl hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              <ArrowLeft className="tw-w-5 tw-h-5" />
              <span className="tw-hidden sm:tw-inline">{previousLabel}</span>
            </button>
          ) : onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-6 tw-py-2 sm:tw-py-3 tw-bg-transparent tw-text-gray-500 tw-text-sm tw-font-medium tw-rounded-xl hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              <X className="tw-w-4 tw-h-4" />
              <span className="tw-hidden sm:tw-inline">Annuler</span>
            </button>
          ) : null}
        </div>

        {modeToggle && (
          <div className="tw-flex-shrink-0">
            {modeToggle}
          </div>
        )}

        <div className="tw-flex tw-items-center tw-gap-2 sm:tw-gap-3 tw-flex-shrink-0 tw-justify-end tw-min-w-0">
          {helpAction}
          {onSaveDraft && (
            <button
              type="button"
              onClick={onSaveDraft}
              disabled={isSavingDraft || isSubmitting}
              className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-5 tw-py-3 tw-bg-white tw-text-affinicar-primary tw-font-medium tw-rounded-xl tw-border tw-border-affinicar-lighter hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              <Save className="tw-w-4 tw-h-4" />
              <span className="tw-hidden sm:tw-inline">{isSavingDraft ? 'Sauvegarde...' : saveLabel}</span>
            </button>
          )}
          <button
            type={formId ? "submit" : "button"}
            form={formId}
            onClick={formId ? undefined : onNext}
            disabled={isSubmitting}
            data-testid="btn-wizard-next"
            className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-8 tw-py-2 sm:tw-py-3 tw-bg-affinicar-primary tw-text-white tw-text-sm sm:tw-text-lg tw-font-semibold tw-rounded-xl tw-shadow-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex-1 sm:tw-flex-initial tw-justify-center sm:tw-justify-start tw-whitespace-nowrap"
          >
            {isSubmitting ? (
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin tw-text-white" />
                <span>{nextLabel}</span>
              </span>
            ) : (
              <>
                {currentStep && totalSteps && (
                  <span className="tw-hidden sm:tw-inline tw-text-white/70 tw-text-sm tw-font-normal">
                    {currentStep}/{totalSteps} —
                  </span>
                )}
                <span>{nextLabel}</span>
                <ArrowRight className="tw-w-5 tw-h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
